import React, { useState, useEffect } from 'react';
import { SubscriptionStatus } from '../types';
import { useSubscription } from '../contexts/SubscriptionContext';
import './SubscriptionBlock.css';

interface SubscriptionBlockProps {
  subscriptionStatus?: SubscriptionStatus | string | number;
  trialDaysRemaining?: number;
  registrationDate?: string;
}

// Função para converter string/número para enum
const parseSubscriptionStatus = (status: SubscriptionStatus | string | number | undefined): SubscriptionStatus | null => {
  if (status === undefined) return null;
  
  if (typeof status === 'number') {
    return status as SubscriptionStatus;
  }
  
  if (typeof status === 'string') {
    const statusMap: Record<string, SubscriptionStatus> = {
      'Trial': SubscriptionStatus.Trial,
      'Paid': SubscriptionStatus.Paid,
      'Cancelled': SubscriptionStatus.Cancelled,
      '0': SubscriptionStatus.Trial,
      '1': SubscriptionStatus.Paid,
      '2': SubscriptionStatus.Cancelled
    };
    return statusMap[status] ?? SubscriptionStatus.Trial;
  }
  
  return status;
};

const SubscriptionBlock: React.FC<SubscriptionBlockProps> = ({
  subscriptionStatus,
  trialDaysRemaining,
  registrationDate
}) => {
  const { isBlocked, blockMessage } = useSubscription();
  const [localBlocked, setLocalBlocked] = useState(false);
  const [localMessage, setLocalMessage] = useState<string | null>(null);

  // Verificar bloqueio do contexto (vindo do backend via 403)
  useEffect(() => {
    if (isBlocked) {
      setLocalBlocked(true);
      setLocalMessage(blockMessage);
    }
  }, [isBlocked, blockMessage]);

  // Verificar bloqueio local (baseado no status do subscription)
  useEffect(() => {
    if (subscriptionStatus !== undefined) {
      const parsedStatus = parseSubscriptionStatus(subscriptionStatus);
      
      if (parsedStatus === SubscriptionStatus.Cancelled) {
        setLocalBlocked(true);
        setLocalMessage('Sua assinatura foi cancelada. Entre em contato com o suporte para reativar sua conta.');
      } else if (parsedStatus === SubscriptionStatus.Trial && (trialDaysRemaining === undefined || trialDaysRemaining <= 0)) {
        setLocalBlocked(true);
        setLocalMessage('Seu período de teste de 30 dias expirou. Entre em contato com o suporte para continuar usando o sistema.');
      } else {
        // Se não estiver bloqueado localmente e não houver bloqueio do contexto, desbloquear
        if (!isBlocked) {
          setLocalBlocked(false);
          setLocalMessage(null);
        }
      }
    }
  }, [subscriptionStatus, trialDaysRemaining, isBlocked]);

  // Listener para eventos de bloqueio do backend
  useEffect(() => {
    const handleBlocked = (event: Event) => {
      const customEvent = event as CustomEvent;
      setLocalBlocked(true);
      setLocalMessage(customEvent.detail?.message || 'Sistema bloqueado');
    };

    window.addEventListener('subscriptionBlocked', handleBlocked);
    return () => window.removeEventListener('subscriptionBlocked', handleBlocked);
  }, []);

  // Verificar localStorage ao montar
  useEffect(() => {
    const blocked = localStorage.getItem('subscriptionBlocked');
    const message = localStorage.getItem('subscriptionBlockMessage');
    if (blocked === 'true') {
      setLocalBlocked(true);
      setLocalMessage(message);
    }
  }, []);

  // Observer para detectar se o elemento foi removido do DOM
  useEffect(() => {
    if (!localBlocked && !isBlocked) return;

    const checkElement = () => {
      const element = document.querySelector('[data-subscription-block="true"]');
      if (!element && (localBlocked || isBlocked)) {
        // Elemento foi removido, forçar re-render
        window.location.reload();
      }
    };

    const observer = new MutationObserver(checkElement);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Verificar periodicamente também
    const interval = setInterval(checkElement, 1000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [localBlocked, isBlocked]);

  if (!localBlocked && !isBlocked) {
    return null;
  }

  const getBlockInfo = () => {
    const message = localMessage || blockMessage || 'Sistema bloqueado';
    
    if (message.includes('cancelada') || message.includes('cancelado')) {
      return {
        title: 'Sistema Bloqueado',
        message: message,
        icon: '🚫'
      };
    }
    
    if (message.includes('expirado') || message.includes('teste')) {
      return {
        title: 'Período de Teste Expirado',
        message: message,
        icon: '⏰'
      };
    }

    return {
      title: 'Sistema Bloqueado',
      message: message,
      icon: '🚫'
    };
  };

  const blockInfo = getBlockInfo();

  return (
    <div className="subscription-block" data-subscription-block="true">
      <div className="subscription-block-content">
        <div className="subscription-block-icon">{blockInfo.icon}</div>
        <h2 className="subscription-block-title">{blockInfo.title}</h2>
        <p className="subscription-block-message">{blockInfo.message}</p>
      </div>
    </div>
  );
};

export default SubscriptionBlock;

